import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  User, 
  Mail, 
  Calendar,
  Search,
  Eye,
  AlertCircle,
  Check,
  X
} from 'lucide-react';
import { ApprovalRequest } from '../types/User';
import { approvalAPI } from '../services/ApprovalAPI';

interface RequestManagementProps {
  requests: ApprovalRequest[];
  onRequestUpdate: (request: ApprovalRequest) => void;
}

const RequestManagement: React.FC<RequestManagementProps> = ({ requests, onRequestUpdate }) => {
  const [activeTab, setActiveTab] = useState<'open' | 'closed'>('open');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [loadedRequests, setLoadedRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingRequestId, setProcessingRequestId] = useState<string | null>(null);
  const [showRejectModal, setShowRejectModal] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Load requests from API
  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading all requests...');
      const result = await approvalAPI.getAllRequests();
      
      console.log('Loaded requests:', result);
      setLoadedRequests(result);
    } catch (err) {
      console.error('Error loading requests:', err);
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const handleApprove = async (request: any) => {
    if (!window.confirm(`Are you sure you want to approve this request for ${request.UserDisplayName}?`)) {
      return;
    }

    try {
      setProcessingRequestId(request.RequestId);
      setError(null);

      const result = await approvalAPI.processApproval({
        requestId: request.RequestId,
        action: 'approve',
        approverEmail: request.ManagerEmail,
        notes: 'Manually approved via admin portal',
        BNAME: request.SapUsername,
        ROLE_NAME: request.RoleName
      });

      if (result.success) {
        // Reload requests to get updated status
        await loadRequests();
        onRequestUpdate(request);
      } else {
        setError(result.message || 'Failed to approve request');
      }
    } catch (err) {
      console.error('Error approving request:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve request');
    } finally {
      setProcessingRequestId(null);
    }
  };

  const handleReject = async (request: any) => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      setProcessingRequestId(request.RequestId);
      setError(null);

      const result = await approvalAPI.processApproval({
        requestId: request.RequestId,
        action: 'reject',
        approverEmail: request.ManagerEmail,
        rejectionReason: rejectionReason,
        notes: 'Manually rejected via admin portal',
        BNAME: request.SapUsername,
        ROLE_NAME: request.RoleName
      });

      if (result.success) {
        // Close modal and reload requests
        setShowRejectModal(null);
        setRejectionReason('');
        await loadRequests();
        onRequestUpdate(request);
      } else {
        setError(result.message || 'Failed to reject request');
      }
    } catch (err) {
      console.error('Error rejecting request:', err);
      setError(err instanceof Error ? err.message : 'Failed to reject request');
    } finally {
      setProcessingRequestId(null);
    }
  };

  const openRejectModal = (requestId: string) => {
    setShowRejectModal(requestId);
    setRejectionReason('');
  };

  const closeRejectModal = () => {
    setShowRejectModal(null);
    setRejectionReason('');
  };

  const filteredRequests = loadedRequests
    .filter(request => {
      const matchesTab = activeTab === 'open' 
        ? request.Status === 'Open' // Only show open requests
        : ['Approved', 'Rejected', 'Completed', 'Failed'].includes(request.Status); // Show closed requests
      
      const matchesSearch = !searchTerm || 
        request.UserDisplayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.RequestedBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.ManagerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.RoleName?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Only apply filter to closed requests
      const matchesFilter = activeTab === 'open' || filterStatus === 'all' || 
        (filterStatus === 'approved' && ['Approved', 'Completed'].includes(request.Status)) ||
        (filterStatus === 'rejected' && request.Status === 'Rejected');
      
      return matchesTab && matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      // Sort by RequestedDate descending (newest first)
      const dateA = new Date(a.RequestedDate).getTime();
      const dateB = new Date(b.RequestedDate).getTime();
      return dateB - dateA;
    });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Open':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'Approved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'Completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'Rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'Failed':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };


  return (
    <div className="card">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Request Management</h3>
        <p className="text-gray-600">Track and manage role change requests</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => {
            setActiveTab('open');
            setFilterStatus('all'); // Reset filter when switching to open tab
          }}
          className={`flex-1 flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'open'
              ? 'bg-white text-primary-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Clock className="h-4 w-4 mr-2" />
          Open Requests ({loadedRequests.filter(r => r.Status === 'Open').length})
        </button>
        <button
          onClick={() => setActiveTab('closed')}
          className={`flex-1 flex items-center justify-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'closed'
              ? 'bg-white text-primary-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Eye className="h-4 w-4 mr-2" />
          Closed Requests ({loadedRequests.filter(r => ['Approved', 'Rejected', 'Completed', 'Failed'].includes(r.Status)).length})
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex space-x-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by user, requester, or manager..."
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        {activeTab === 'closed' && (
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading requests...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
            <button 
              onClick={() => setError(null)} 
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Requests List */}
      {!loading && (
        <div className="space-y-4">
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No {activeTab === 'open' ? 'open' : 'closed'} requests found
              </h3>
              <p className="text-gray-600">
                {activeTab === 'open' 
                  ? 'All requests have been processed'
                  : 'No completed requests match your search criteria'
                }
              </p>
            </div>
          ) : (
          filteredRequests.map((request) => (
            <div key={request.RequestId} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <User className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="font-medium text-gray-900">{request.UserDisplayName}</span>
                    <span className="mx-2 text-gray-400">•</span>
                    <span className="text-sm text-gray-600">{request.RequestType} - {request.RoleName}</span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                    <div>
                      <div className="flex items-center mb-1">
                        <Mail className="h-3 w-3 mr-1" />
                        <span>Requested by: {request.RequestedBy}</span>
                      </div>
                      <div className="flex items-center">
                        <User className="h-3 w-3 mr-1" />
                        <span>Manager: {request.ManagerName}</span>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center mb-1">
                        <Calendar className="h-3 w-3 mr-1" />
                        <span>Requested: {new Date(request.RequestedDate).toLocaleDateString()}</span>
                      </div>
                      {request.approvedAt && (
                        <div className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          <span>
                            {request.status === 'approved' ? 'Approved' : 'Rejected'}: {request.approvedAt.toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {request.Justification && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Justification:</span> {request.Justification}
                      </p>
                    </div>
                  )}
                </div>

                <div className="ml-4 flex flex-col items-end space-y-3">
                  <div className={`px-3 py-1 rounded-full text-sm font-medium border ${
                    request.Status === 'Open' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' :
                    request.Status === 'Approved' ? 'bg-green-50 border-green-200 text-green-800' :
                    request.Status === 'Completed' ? 'bg-green-50 border-green-200 text-green-800' :
                    request.Status === 'Rejected' ? 'bg-red-50 border-red-200 text-red-800' :
                    request.Status === 'Failed' ? 'bg-red-50 border-red-200 text-red-800' :
                    'bg-gray-50 border-gray-200 text-gray-800'
                  }`}>
                    <div className="flex items-center">
                      {getStatusIcon(request.Status)}
                      <span className="ml-1">{request.Status}</span>
                    </div>
                  </div>

                  {/* Approve/Reject Buttons for Open Requests */}
                  {request.Status === 'Open' && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleApprove(request)}
                        disabled={processingRequestId === request.RequestId}
                        className="flex items-center px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {processingRequestId === request.RequestId ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-1" />
                            Approve
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => openRejectModal(request.RequestId)}
                        disabled={processingRequestId === request.RequestId}
                        className="flex items-center px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
          )}
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Request</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejecting this request:
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent mb-4"
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeRejectModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const request = loadedRequests.find(r => r.RequestId === showRejectModal);
                  if (request) {
                    handleReject(request);
                  }
                }}
                disabled={!rejectionReason.trim() || processingRequestId === showRejectModal}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {processingRequestId === showRejectModal ? 'Processing...' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestManagement;
